import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { useState, useRef, useMemo } from 'react';
import useLayoutEffect from 'use-isomorphic-layout-effect';
import usePrevious from 'use-previous';
import { omitUndefined } from '@remirror/core';
import { getPositioner, PositionerExtension, defaultAbsolutePosition } from '@remirror/extension-positioner';
import { useExtension } from '@remirror/react-core';

/**
 * A positioner for your editor. This returns an array of active positions and
 * is useful for tracking the positions of multiple items in the editor.
 *
 * ```ts
 * import { Positioner } from 'remirror/extensions';
 * import { useMultiPositioner } from '@remirror/react';
 *
 * const positioner = Positioner.create({
 *   ...config, // custom config
 * })
 *
 * const MenuComponent: FC = () => {
 *   const positions = usePositioner(positioner, []);
 *
 *   return (
 *     <>
 *       {
 *         positions.map(({ ref, bottom, left, key }) => (
 *           <div style={{ bottom, left }} ref={ref} key={key}>
 *             <MenuIcon {...options} />
 *           </div>
 *         )
 *       }
 *     </>
 *   )
 * };
 * ```
 *
 * @param positioner - the positioner which will be used
 * @param deps - an array of dependencies which will cause the hook to rerender
 * with an updated positioner. This is the only way to update the positioner.
 */
function useMultiPositioner(positioner, deps) {
  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var _useState3 = useState(() => getPositioner(positioner)),
      _useState4 = _slicedToArray(_useState3, 2),
      memoizedPositioner = _useState4[0],
      setMemoizedPositioner = _useState4[1];

  var _useState5 = useState([]),
      _useState6 = _slicedToArray(_useState5, 2),
      collectRefs = _useState6[0],
      setCollectRefs = _useState6[1];

  var positionerRef = useRef(positioner);
  var previousPositioner = usePrevious(memoizedPositioner);
  positionerRef.current = positioner;
  useExtension(PositionerExtension, _ref => {
    var addCustomHandler = _ref.addCustomHandler;
    var positioner = getPositioner(positionerRef.current);
    var dispose = addCustomHandler('positioner', positioner);
    setMemoizedPositioner(positioner);
    return dispose;
  }, deps); // Add the positioner update handlers.

  useLayoutEffect(() => {
    var disposeUpdate = memoizedPositioner.addListener('update', options => {
      var items = [];

      var _iterator = _createForOfIteratorHelper(options),
          _step;

      try {
        var _loop = function _loop() {
          var _step$value = _step.value,
              id = _step$value.id,
              setElement = _step$value.setElement;

          var ref = element => {
            if (!element) {
              return;
            }

            setElement(element);
          };

          items.push({
            id,
            ref
          });
        };

        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          _loop();
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }

      setCollectRefs(items);
    });
    var disposeDone = memoizedPositioner.addListener('done', options => {
      setState(options);
    });

    if (previousPositioner !== null && previousPositioner !== void 0 && previousPositioner.recentUpdate) {
      memoizedPositioner.onActiveChanged(previousPositioner === null || previousPositioner === void 0 ? void 0 : previousPositioner.recentUpdate);
    }

    return () => {
      disposeUpdate();
      disposeDone();
    };
  }, [memoizedPositioner, previousPositioner]);
  return useMemo(() => {
    var positions = [];

    var _iterator2 = _createForOfIteratorHelper(collectRefs.entries()),
        _step2;

    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var _step2$value = _slicedToArray(_step2.value, 2),
            index = _step2$value[0],
            _step2$value$ = _step2$value[1],
            ref = _step2$value$.ref,
            key = _step2$value$.id;

        var stateValue = state[index];

        var _ref2 = stateValue !== null && stateValue !== void 0 ? stateValue : {},
            element = _ref2.element,
            _ref2$position = _ref2.position,
            position = _ref2$position === void 0 ? {} : _ref2$position;

        var absolutePosition = _objectSpread(_objectSpread({}, defaultAbsolutePosition), omitUndefined(position));

        positions.push(_objectSpread({
          ref,
          element,
          key
        }, absolutePosition));
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }

    return positions;
  }, [collectRefs, state]);
}

export { useMultiPositioner };
